import React, { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

const VARIANTS = ['primary', 'secondary', 'outline', 'ghost', 'success', 'danger', 'warning'];

/**
 * Enterprise button. One strong `primary` per visual area; everything else
 * should be `secondary`, `outline` or `ghost`.
 */
const Button = forwardRef(function Button(
    {
        children,
        variant = 'secondary',
        size = 'md',
        icon: Icon,
        iconRight: IconRight,
        loading = false,
        block = false,
        className = '',
        type = 'button',
        disabled,
        ...props
    },
    ref
) {
    const v = VARIANTS.includes(variant) ? variant : 'secondary';
    const iconSize = size === 'sm' ? 13 : 15;

    return (
        <button
            ref={ref}
            type={type}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            className={[
                'btn',
                `btn-${v}`,
                size !== 'md' ? `btn-${size}` : '',
                block ? 'btn-block' : '',
                !children ? 'btn-icon' : '',
                className,
            ]
                .filter(Boolean)
                .join(' ')}
            {...props}
        >
            {loading ? (
                <Loader2 size={iconSize} className="animate-spin" aria-hidden="true" />
            ) : (
                Icon && <Icon size={iconSize} aria-hidden="true" />
            )}
            {children}
            {IconRight && !loading && <IconRight size={iconSize} aria-hidden="true" />}
        </button>
    );
});

/** Square, label-less button. `label` is required for screen readers. */
export const IconButton = forwardRef(function IconButton(
    { icon: Icon, label, size = 'md', variant = 'ghost', className = '', ...props },
    ref
) {
    return (
        <Button
            ref={ref}
            variant={variant}
            size={size}
            aria-label={label}
            title={label}
            className={`btn-icon ${className}`}
            {...props}
        >
            {Icon && <Icon size={size === 'sm' ? 14 : 16} aria-hidden="true" />}
        </Button>
    );
});

export default Button;
